import {
  useEssentials,
  useRouterContext,
  getReferralInfoFromCurrentUrl,
} from '@wix/app-market-core';
import {
  appMarketAppPageAppGalleryClick,
  appMarketBackButtonClick,
  appMarketAppPageClick,
  appMarketCategorySubCategoryClickFromTheAppPageSrc24Evid159,
  appMarketInContextAppPageSeeFullDescription,
} from '@wix/bi-logger-app-market-data/v2';
import { RoutePath } from '@wix/app-market-core-types';
import type { RoutePayloadMap } from '@wix/app-market-core-types';
import type { AppPageType } from '@wix/app-market-core-components-types';

interface QuickInfoActions {
  onBack(appId: string): void;
  onTagClick(
    categorySlug: string,
    subCategory: string,
    appId: string,
    label: 'category' | 'sub_category',
    location: 'top_page' | 'side_category_page',
  ): void;
  onGalleryChange(oldIndex: number, newIndex: number, appId: string): void;
  onCompanyClick(companyName: string, companySlug: string, appId: string): void;
  onReviewsClick(appId: string, reviewsAmount: number): void;
  onExpandDescriptionButtonClick(appId: string, isExpanded: boolean): void;
}

export const useQuickInfoActions = (
  appPageType: AppPageType,
): QuickInfoActions => {
  const { biLogger } = useEssentials();
  const { router } = useRouterContext();

  function onBack(appId: string) {
    biLogger.report(
      appMarketBackButtonClick({
        appId,
        tag_type: 'app_page',
        tag_name: 'app_id',
        location: 'page',
        app_page_type: appPageType,
      }),
    );
    router.hasHistory() &&
      router.navigateTo({
        path: RoutePath.BACK,
        payload: undefined,
      });
  }

  function onTagClick(
    categorySlug: string,
    subCategorySlug: string,
    appId: string,
    label: 'category' | 'sub_category',
    location: 'top_page' | 'side_category_page',
  ) {
    biLogger.report(
      appMarketCategorySubCategoryClickFromTheAppPageSrc24Evid159({
        category_name: categorySlug,
        subcategory_name: subCategorySlug,
        label_type: label,
        app_id: appId,
        app_page_location: location,
        app_page_type: appPageType,
      }),
    );
    const categoryNavigation = {
      path: RoutePath.CATEGORY as RoutePath.CATEGORY,
      payload: {
        slug: categorySlug,
      } as RoutePayloadMap[RoutePath.CATEGORY],
    };
    const subCategoryNavigation = {
      path: RoutePath.SUB_CATEGORY as RoutePath.SUB_CATEGORY,
      payload: {
        parentSlug: categorySlug,
        slug: subCategorySlug,
        subCat: subCategorySlug,
      } as RoutePayloadMap[RoutePath.SUB_CATEGORY],
    };
    router.navigateTo(
      label === 'sub_category' ? subCategoryNavigation : categoryNavigation,
    );
  }

  function onGalleryChange(oldIndex: number, newIndex: number, appId: string) {
    const action = oldIndex < newIndex ? 'right' : 'left';
    biLogger.report(
      appMarketAppPageAppGalleryClick({
        action,
        app_id: appId,
        tag_name: 'app_page',
        app_page_type: appPageType,
      }),
    );
  }

  function onCompanyClick(
    companyName: string,
    companySlug: string,
    appId: string,
  ) {
    biLogger.report(
      appMarketAppPageClick({
        app_id: appId,
        cta: 'company_name',
        location: 'info',
        value: companyName,
        app_page_type: appPageType,
      }),
    );
    router.navigateTo({
      path: RoutePath.DEVELOPER,
      payload: {
        slug: companySlug,
      },
    });
  }

  function onReviewsClick(appId: string, reviewsAmount: number) {
    biLogger.report(
      appMarketAppPageClick({
        app_id: appId,
        cta: 'review_number',
        location: 'info',
        value: reviewsAmount.toString(),
        app_page_type: appPageType,
      }),
    );
    scrollToReviewsSection();
  }

  function onExpandDescriptionButtonClick(appId: string, isExpand: boolean) {
    biLogger.report(
      appMarketInContextAppPageSeeFullDescription({
        app_id: appId,
        referral_info: getReferralInfoFromCurrentUrl(),
        app_page_type: appPageType,
      }),
    );
    biLogger.report(
      appMarketAppPageClick({
        app_id: appId,
        cta: isExpand ? 'open_additional_info' : 'close_additional_info',
        location: 'info',
        app_page_type: appPageType,
      }),
    );
  }

  return {
    onBack,
    onTagClick,
    onCompanyClick,
    onGalleryChange,
    onReviewsClick,
    onExpandDescriptionButtonClick,
  };
};

function scrollToReviewsSection() {
  const reviewsDivider = document.querySelector(
    '[data-hook="reviews-top-divider"]',
  );
  const stickyHeaderElement = document.querySelector(
    '[data-hook="app-page-sticky"]',
  );
  if (!reviewsDivider || !stickyHeaderElement) {
    return;
  }
  const scrollableAncestor = getScrollableAncestor(reviewsDivider);

  const distanceBetweenReviewsDividerToTopOfViewport =
    reviewsDivider.getBoundingClientRect().top;
  const distanceBetweenStickyHeaderBottomToTopOfViewport =
    stickyHeaderElement.getBoundingClientRect().bottom;

  scrollableAncestor.scroll({
    top:
      distanceBetweenReviewsDividerToTopOfViewport -
      distanceBetweenStickyHeaderBottomToTopOfViewport,
    behavior: 'smooth',
  });
}

function getScrollableAncestor(element: Element | null): Element | Window {
  if (!element) {
    return window;
  }

  const { overflowY } = window.getComputedStyle(element);

  const isElementScrollable =
    /(auto|scroll)/.test(overflowY) &&
    element.scrollHeight > element.clientHeight;

  if (isElementScrollable) {
    return element;
  }

  return getScrollableAncestor(element.parentElement);
}
